import {
  Box,
  CardMedia,
  Divider,
  Icon,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'

// api
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { getSpecificJobBoard } from 'services/api/job-board'

// assets
import JobBoardFooter from '../components/JobBoardFooter'
import notFoundIcon from '../assets/Union.svg'

// components
import CopyLinkModal from '../components/CopyLinkModal'
import JobBoardApplicationModal from '../components/JobBoardApplicationModal'
import MobileJobBoardApplicationDrawer from '../components/MobileJobBoardApplicationDrawer'

const SpecificJobBoard = () => {
  const params = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const company_uuid = params?.uuid
  const company_name = params?.companyName

  // API calls
  const {
    isLoading: specificJobBoardIsLoading,
    isError: specificJobBoardIsError,
    data: specificJobBoardData,
  } = useQuery(
    ['getSpecificJobBoard', company_uuid, params.id],
    async () => {
      if (params.id) {
        const { data } = await getSpecificJobBoard(company_uuid, params.id)

        return data
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const date = new Date(specificJobBoardData?.created_at)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based, so we add 1
  const day = String(date.getDate()).padStart(2, '0')

  const formattedDate = `${year}-${month}-${day}`

  const splitWord = (text) => {
    if (text) {
      // Replace hyphens used as bullet points with actual bullet points
      let replacedText = text.replace(/^\s*-\s*/gm, '• ')

      // Handle numbered points by inserting a newline before each number
      replacedText = replacedText.replace(/(\d+\.)/g, '\n$1')

      const arr = replacedText.split('\n')
      const cleanedArr = arr.filter((point) => point.trim() !== '')
      const formattedArr = cleanedArr.map((point) => {
        // Check if the point starts with a bullet or a number
        if (point.trim().startsWith('•') || /^\d+\./.test(point.trim())) {
          return point.trim()
        } else {
          return '• ' + point.trim()
        }
      })

      return formattedArr
    }

    return text
  }

  const jobDescription = splitWord(specificJobBoardData?.job_description)
  const jobRequirement = splitWord(specificJobBoardData?.job_requirement)

  const workExperience = (freshGraduate, yearsOfExperience) => {
    if (freshGraduate === 1) {
      return 'Fresh graduates are welcomed to apply'
    } else if (yearsOfExperience === 1) {
      return '0-1 year'
    } else if (yearsOfExperience === 2) {
      return '1-3 years'
    } else if (yearsOfExperience === 3) {
      return '3-5 years'
    } else {
      return 'More than 5 years'
    }
  }

  const skillsArray = specificJobBoardData?.skill?.split(',')
  const skills = skillsArray?.map((element) => element?.trim())

  if (specificJobBoardIsLoading) {
    return <SpecificJobBoardLoadingState isMobile={isMobile} />
  } else if (specificJobBoardIsError) {
    return <SpecificJobBoardEmptyState isMobile={isMobile} />
  } else
    return (
      <>
        <Box
          sx={{
            padding: isMobile ? '20px' : '64px 116px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
              <Icon sx={{ color: 'alternate.main', fontSize: '12px' }}>chevron_left</Icon>
              <Typography
                variant="subtitle2"
                sx={{ color: 'alternate.main' }}
                onClick={() => navigate(`/job-board/${company_name}/${company_uuid}`)}
              >
                All Jobs
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ color: '#B8B8B8' }}>
              {`Posted on ${formattedDate}`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Box>
                <CardMedia
                  component="img"
                  sx={{
                    width: '88px',
                    height: '88px',
                    objectFit: 'contain',
                    border: 1,
                    borderRadius: '8px',
                    borderColor: 'gray-line.main',
                  }}
                  src={specificJobBoardData?.organization_logo_url}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '4px',
                }}
              >
                <Typography fontFamily={'Oswald'} fontWeight={600} variant="h1">
                  {specificJobBoardData?.position_title}
                </Typography>
                <Typography variant="h4" sx={{ color: 'light-gray-text.main' }}>
                  {specificJobBoardData?.job_level_name}
                </Typography>
              </Box>
            </Box>
            {!isMobile && <JobBoardApplicationModal />}
          </Box>
          {isMobile && (
            <Box sx={{ width: '100%' }}>
              <MobileJobBoardApplicationDrawer />
            </Box>
          )}
          <Box sx={{ width: '100%' }}>
            <Divider />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              gap: '24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
                Job Details
              </Typography>
              <CopyLinkModal />
            </Box>
            <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                >
                  Position level
                </Typography>
                <Typography
                  sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                >
                  Specialization
                </Typography>
                <Typography
                  sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                >
                  Employment type
                </Typography>
                <Typography
                  sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                >
                  Salary
                </Typography>
                <Typography sx={{ color: 'light-gray-text.main', whiteSpace: 'nowrap' }}>
                  Work location
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ height: '30px' }}>
                  {specificJobBoardData?.job_level_name}
                </Typography>
                <Typography sx={{ height: '30px' }}>
                  {specificJobBoardData?.job_background_name}
                </Typography>
                <Typography sx={{ height: '30px' }}>
                  {specificJobBoardData?.position_type === 'full-time'
                    ? 'Full time'
                    : specificJobBoardData?.position_type === 'part-time'
                    ? 'Part time'
                    : 'Contract'}
                </Typography>
                <Typography
                  sx={{ height: '30px' }}
                >{`RM ${specificJobBoardData?.min_salary} - RM ${specificJobBoardData?.max_salary}`}</Typography>
                <Typography>{specificJobBoardData?.work_location}</Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              gap: '24px',
            }}
          >
            <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
              Job Description
            </Typography>
            <Box display="flex" flexDirection="column" sx={{ gap: '16px' }}>
              {Array?.isArray(jobDescription) ? (
                jobDescription?.map((item, index) => <Typography key={index}>{item}</Typography>)
              ) : (
                <Typography>{specificJobBoardData?.job_description}</Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '24px',
            }}
          >
            <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
              Job Requirements
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography
                    sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                  >
                    Qualification
                  </Typography>
                  <Typography
                    sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                  >
                    Work experience
                  </Typography>
                  <Typography
                    sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
                  >
                    Skills
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{ height: '30px' }}>
                    {specificJobBoardData?.qualification}
                  </Typography>
                  <Typography sx={{ height: '30px' }}>
                    {workExperience(
                      specificJobBoardData?.fresh_grade_placement,
                      specificJobBoardData?.year_of_experience
                    )}
                  </Typography>
                  {!isMobile && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                    >
                      {skills?.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            borderRadius: '3.5px',
                            backgroundColor: 'alternate.light',
                            padding: '6px 12px',
                            textAlign: 'center',
                          }}
                        >
                          <Typography sx={{ color: 'alternate.main', whiteSpace: 'nowrap' }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {skills?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        borderRadius: '3.5px',
                        backgroundColor: 'alternate.light',
                        padding: '6px 12px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ color: 'alternate.main', whiteSpace: 'nowrap' }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box display="flex" flexDirection="column" sx={{ gap: '16px', marginTop: '16px' }}>
              {Array?.isArray(jobRequirement) ? (
                jobRequirement?.map((item, index) => <Typography key={index}>{item}</Typography>)
              ) : (
                <Typography>{specificJobBoardData?.job_requirement}</Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '32px',
            }}
          >
            {!isMobile && <JobBoardApplicationModal />}
          </Box>
        </Box>
        <JobBoardFooter />
      </>
    )
}

export default SpecificJobBoard

export const SpecificJobBoardEmptyState = ({ isMobile }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box
        sx={{
          height: '140px',
          width: '100%',
          background: `linear-gradient(to right, #104C4C 0%, rgba(0, 0, 10, 0.9) 25%, rgba(0, 0, 10, 0.9) 75%, #104C4C 100%)`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingX: '60px',
        }}
      >
        <Typography fontFamily={'Oswald'} variant="h2" sx={{ color: 'white.main' }}>
          404 Not Found
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: '20px',
          paddingY: '100px',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'default-background.main',
            borderRadius: '100%',
            padding: isMobile ? '30px' : '80px',
          }}
        >
          <CardMedia
            component="img"
            sx={{
              width: isMobile ? '90px' : '160px',
              height: isMobile ? '90px' : '160px',
              objectFit: 'contain',
              borderRadius: '100%',
            }}
            src={notFoundIcon}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          <Typography>This page doesn't exist.</Typography>
          <Typography sx={{ color: 'light-gray-text.main' }}>
            Sorry, we couldn't find the URL on our server.
          </Typography>
        </Box>
      </Box>
      <JobBoardFooter />
    </Box>
  )
}

export const SpecificJobBoardLoadingState = ({ isMobile }) => {
  return (
    <>
      <Box
        sx={{
          padding: isMobile ? '20px' : '64px 116px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
            <Icon sx={{ color: 'alternate.main', fontSize: '12px' }}>chevron_left</Icon>
            <Typography variant="subtitle2" sx={{ color: 'alternate.main' }}>
              <Skeleton variant="text" width={'30px'} />
            </Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ color: '#B8B8B8' }}>
            <Skeleton variant="text" width={'100px'} />
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box>
              <Skeleton variant="rounded" width={'88px'} height={'88px'} />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '4px',
              }}
            >
              <Typography fontFamily={'Oswald'} fontWeight={600} variant="h1">
                <Skeleton variant="text" width={'30%'} />
              </Typography>
              <Typography variant="h4" sx={{ color: 'light-gray-text.main' }}>
                <Skeleton variant="text" width={'30%'} />
              </Typography>
            </Box>
          </Box>
          {!isMobile && <Skeleton variant="rounded" width={'100px'} />}
        </Box>
        <Box sx={{ width: '100%' }}>
          {isMobile && <Skeleton variant="rounded" width={'100%'} />}
        </Box>
        <Box sx={{ width: '100%' }}>
          <Divider />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '24px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
              <Skeleton variant="text" width={'300px'} />
            </Typography>
            <Skeleton variant="rounded" width="100px" />
          </Box>
          <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width={'115px'} />
              </Typography>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width={'115px'} />
              </Typography>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width={'115px'} />
              </Typography>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width={'115px'} />
              </Typography>
              <Typography sx={{ color: 'light-gray-text.main', whiteSpace: 'nowrap' }}>
                <Skeleton variant="text" width={'115px'} />
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Typography>
                <Skeleton variant="text" />
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            gap: '24px',
          }}
        >
          <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
            <Skeleton variant="text" width={'40%'} />
          </Typography>
          <Box display="flex" flexDirection="column" sx={{ gap: '16px' }}>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '24px',
          }}
        >
          <Typography fontFamily={'Oswald'} fontWeight={600} variant="h2">
            <Skeleton variant="text" width={'40%'} />
          </Typography>
          <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width="115px" />
              </Typography>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width="115px" />
              </Typography>
              <Typography
                sx={{ color: 'light-gray-text.main', height: '30px', whiteSpace: 'nowrap' }}
              >
                <Skeleton variant="text" width="115px" />
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Typography sx={{ height: '30px' }}>
                <Skeleton variant="text" />
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Skeleton variant="rounded" width="150px" />
                <Skeleton variant="rounded" width="150px" />
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" sx={{ gap: '16px', marginTop: '16px' }}>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '32px',
          }}
        >
          <Skeleton variant="rounded" width="100px" />
        </Box>
      </Box>
      <JobBoardFooter />
    </>
  )
}
